import React, { useEffect, useRef, useState, useCallback } from 'react'
import SimpleKeyboard from 'simple-keyboard'
import cogoToast from '@successtar/cogo-toast'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import Button from './Button'
import 'simple-keyboard/build/css/index.css'
import { Html5Qrcode, Html5QrcodeResult } from 'html5-qrcode'
import { unifiedCameraUtils } from '@renderer/utils/cameras/uniCamUtils'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import Preview from '@renderer/components/Preview'
import { formatAmount, normalizeCurrency } from '@renderer/utils/formatCurrency'

interface PromoModalProps {
  showModal: boolean
  setShowModal: (value: boolean) => void
  onPromoApplied: (discount: number) => void
  /** Session subtotal for cash voucher validation and amount. Default 100000 if not provided. */
  subtotal?: number
}

type InputMode = 'keyboard' | 'qr'

const PromoModal: React.FC<PromoModalProps> = ({
  showModal,
  setShowModal,
  onPromoApplied,
  subtotal = 100000
}) => {
  const [input, setInput] = useState<string>('')
  const keyboardRef = useRef<SimpleKeyboard | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const qrScannerRef = useRef<Html5Qrcode | null>(null)
  const qrElementRef = useRef<HTMLDivElement>(null)
  const scanIntervalRef = useRef<NodeJS.Timeout | null>(null)

  const [inputMode, setInputMode] = useState<InputMode>('keyboard')
  const [isScanning, setIsScanning] = useState(false)
  const [scanError, setScanError] = useState<string>('')

  const { t } = useTranslation()
  const { projectId } = useParams()

  const configCustomFramePrice = useConfigStore(
    (s) => s.config?.pages?.checkout?.settings?.customFramePrice
  )
  const projectCurrency = useConfigStore((s) =>
    normalizeCurrency(s.config?.resolvedCurrency)
  )

  const cameraKey =
    (useSessionStateStore().userPreferences.selectedCamera as 'primary' | 'secondary') || 'primary'

  const handleSubmitPromo = async (input: string): Promise<void> => {
    try {
      const response = await fetch(`${process.env.VITE_API_URL}/api/promos/apply`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: input,
          projectId,
          amount: Math.round(subtotal)
        })
      })

      const data = await response.json()
      const isValid = response.ok && data.isValid
      const linkedFrameId =
        typeof data.linkedFrameId === 'string' && data.linkedFrameId.length > 0
          ? data.linkedFrameId
          : undefined
      const surchargePerPackage = Number(data.surchargePerPackage) || 0

      if (isValid) {
        if (linkedFrameId) {
          const { setSessionState } = useSessionStateStore.getState()
          const roundedSubtotal = Math.round(subtotal)
          // Use the project-configured price from configState if available; fall back to API value.
          const finalSurcharge = configCustomFramePrice ?? surchargePerPackage

          // Inject the voucher-linked frame into the config store so it renders and
          // can be pinned/selected even when this project's own frame list omits it.
          // Projects without a frame picker (e.g. bahela receipt) simply ignore it.
          const linked = data.linkedFrame
          if (linked && typeof linked._id === 'string' && linked.drawParams) {
            useConfigStore.getState().ensureFrame({
              _id: linked._id,
              name: typeof linked.name === 'string' ? linked.name : 'Custom frame',
              isActive: true,
              isPublic: true,
              isCustomFrame: true,
              owner: typeof linked.owner === 'string' ? linked.owner : '',
              tags: Array.isArray(linked.tags) ? linked.tags : [],
              thumbnail: typeof linked.thumbnail === 'string' ? linked.thumbnail : '',
              drawParams: linked.drawParams
            })
          }

          setSessionState({
            userPreferences: {
              frameId: linkedFrameId,
              customFrameSurchargePerPackage: finalSurcharge,
              // Custom-frame voucher includes recording free of charge; force it on
              // and lock the toggle at checkout.
              useRecording: true,
              customFrameRecordingLocked: true
            },
            transaction: {
              ...(data.isCash
                ? { amount: roundedSubtotal, discount: 0 }
                : { discount: data.discount ?? 0 }),
              voucherCode: input.toUpperCase(),
              voucherId: data.promoId,
              voucherType: data.isCash ? 'cash' : data.type,
              voucherValue: data.value != null ? data.value : undefined,
              status: data.isCash ? 'settlement' : undefined
            }
          })
          onPromoApplied(data.isCash ? 0 : data.discount ?? 0)
          cogoToast.success(
            t(
              'bahela.promoModal.customFrameVoucherApplied',
              'Custom frame voucher applied. Your frame will be selected at the kiosk.'
            )
          )
          setShowModal(false)
          setInput('')
          return
        }

        let discountMessage = ''
        let finalDiscount = 0

        if (data.isCash) {
          // Cash voucher: revenue = session price (subtotal), no discount
          finalDiscount = 0
          discountMessage = t('bahela.promoModal.cashVoucherApplied', 'Cash voucher applied.')
        } else if (data.type === 'fixed') {
          // For fixed amount, store positive value
          finalDiscount = data.discount
          discountMessage = t('bahela.promoModal.fixedDiscountApplied', {
            value: formatAmount(finalDiscount, projectCurrency)
          })
        } else {
          // For percentage, store negative value
          const percentageValue = subtotal > 0 ? (data.discount / subtotal) * 100 : 0
          finalDiscount = -percentageValue // Store as negative to indicate percentage
          discountMessage = t('bahela.promoModal.percentageDiscountApplied', {
            value: Math.abs(finalDiscount)
          })
        }

        // Store voucher information in session state
        const { setSessionState } = useSessionStateStore.getState()
        const roundedSubtotal = Math.round(subtotal)
        setSessionState({
          transaction: {
            ...(data.isCash ? { amount: roundedSubtotal, discount: 0 } : { discount: finalDiscount }),
            voucherCode: input.toUpperCase(),
            voucherId: data.promoId,
            voucherType: data.isCash ? 'cash' : data.type,
            voucherValue: data.value != null ? data.value : undefined,
            status: data.isCash ? 'settlement' : undefined
          }
        })

        onPromoApplied(finalDiscount)
        cogoToast.success(discountMessage)
        setShowModal(false)
        setInput('')
      } else {
        cogoToast.error(data.message || t('bahela.promoModal.invalidPromo'))
      }
    } catch (error) {
      console.error('Error validating promo code:', error)
      cogoToast.error(t('bahela.promoModal.promoError'))
    }
  }

  useEffect(() => {
    if (showModal && inputMode === 'keyboard') {
      keyboardRef.current = new SimpleKeyboard({
        onChange: (input: string): void => setInput(input),
        onKeyPress: (button: string): void => {
          if (button === '{bksp}') {
            // Handled by onChange
          } else if (button === '{enter}') {
            handleSubmitPromo(input)
          }
        },
        layout: {
          default: [
            '` 1 2 3 4 5 6 7 8 9 0 - = {bksp}',
            '{tab} Q W E R T Y U I O P { } |',
            '{lock} A S D F G H J K L : " {enter}',
            '{shift} Z X C V B N M &lt; &gt; ? {shift}',
            '.com @ {space}'
          ]
        },
        theme: 'hg-theme-default hg-layout-default myTheme',
        display: {
          '{bksp}': '⌫',
          '{enter}': '✓',
          '{space}': ' ',
          '{shift}': '⇧',
          '{tab}': '⇥',
          '{lock}': '⇪'
        },
        input: input
      })

      return (): void => keyboardRef.current?.destroy()
    }
    return
  }, [inputMode])

  // QR Code scanning logic using camera preview
  const scanQRFromCamera = useCallback(async (): Promise<void> => {
    try {
      // Capture image from camera
      const blob = await unifiedCameraUtils.capturePreview(cameraKey, {
        maxWidth: 1920,
        maxHeight: 1080,
        quality: 0.9
      })

      if (!blob) {
        setScanError(t('checkout.failedToCaptureImage', 'Failed to capture image from camera'))
        return
      }

      // Convert blob to File for Html5Qrcode
      const file = new File([blob], 'qr-scan.jpg', { type: blob.type })

      // Create QR scanner instance if not exists
      if (!qrScannerRef.current && qrElementRef.current) {
        qrScannerRef.current = new Html5Qrcode('qr-scanner-element')
      }

      if (qrScannerRef.current) {
        try {
          // Scan the captured image for QR code
          const result: Html5QrcodeResult = await qrScannerRef.current.scanFileV2(file, false)

          if (result && result.decodedText) {
            // QR code found! Apply the decoded text as promo code
            setInput(result.decodedText)
            setScanError('')

            // Auto-apply the promo code
            setTimeout(() => {
              handleSubmitPromo(result.decodedText)
            }, 500)
          }
        } catch (scanErr) {
          // No QR code found in this frame, continue scanning
          if (!scanErr) {
            console.log(scanErr)
          }
          setScanError('')
        }
      }
    } catch (error) {
      console.error('QR scan error:', error)
      setScanError(t('checkout.errorScanningQR', 'Error scanning QR code'))
    }
  }, [cameraKey])

  // Start QR scanning
  const startQRScanning = useCallback((): void => {
    setIsScanning(true)
    setScanError('')

    // Scan every 500ms
    scanIntervalRef.current = setInterval(() => {
      scanQRFromCamera()
    }, 500)
  }, [scanQRFromCamera])

  // Stop QR scanning
  const stopQRScanning = useCallback((): void => {
    setIsScanning(false)

    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current)
      scanIntervalRef.current = null
    }
  }, [])

  // Handle mode switching
  const switchToKeyboard = (): void => {
    stopQRScanning()
    setInputMode('keyboard')
  }

  const switchToQR = (): void => {
    setInputMode('qr')
    startQRScanning()
  }

  // Cleanup on unmount
  useEffect(() => {
    return (): void => {
      stopQRScanning()
      if (qrScannerRef.current) {
        qrScannerRef.current.clear()
        qrScannerRef.current = null
      }
    }
  }, [])

  if (!showModal) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex flex-col items-center justify-center z-[10]">
      <div className="absolute left-[50px] top-[50px] flex flex-col items-center">
        <button
          type="button"
          className=""
          onClick={() => {
            setShowModal(false)
            setInput('')
            stopQRScanning()
            setInputMode('keyboard')
          }}
        >
          <img
            src="/assets/bahela/cancel.svg"
            className="w-[80px] h-[80px] btn-shadow"
            alt={t('bahela.promoModal.cancel')}
          />
        </button>
        <span className="font-body-bahela text-[24px] text-white">
          {t('bahela.promoModal.cancel')}
        </span>
      </div>
      <h2 className="text-white text-[64px] font-serif-bahela mb-4 text-center">
        {t('bahela.promoModal.title')}
      </h2>

      {/* Mode Toggle Buttons */}
      <div className="flex gap-2 mb-8 md:w-auto w-3/5">
        <Button
          bgColor={inputMode === 'keyboard' ? 'uriYellow' : 'transparent'}
          textColor={inputMode === 'keyboard' ? 'hibiscus' : 'uriYellow'}
          onClick={switchToKeyboard}
          className="flex-1 md:flex-none !text-lg md:!text-xl !px-4 md:!px-6 !py-2 md:!py-3"
        >
          {t('checkout.keyboard', 'Keyboard')}
        </Button>
        <Button
          bgColor={inputMode === 'qr' ? 'uriYellow' : 'transparent'}
          textColor={inputMode === 'qr' ? 'hibiscus' : 'uriYellow'}
          onClick={switchToQR}
          className="flex-1 md:flex-none !text-lg md:!text-xl !px-4 md:!px-6 !py-2 md:!py-3"
        >
          {t('checkout.scanQR', 'Scan QR')}
        </Button>
      </div>

      <div className="flex flex-col items-center w-3/5 mb-64 gap-8">
        {inputMode === 'keyboard' && (
          <input
            ref={inputRef}
            className="flex-1 border-[10px] border-uriYellow rounded-full p-4 text-[28px] font-bold text-center text-gray-800"
            placeholder={t('bahela.promoModal.placeholder')}
            value={input}
            onInput={(e: React.ChangeEvent<HTMLInputElement>) => setInput(e.target.value)}
          />
        )}

        {inputMode === 'qr' && (
          <div className="w-full h-[300px] md:h-[400px] bg-gray-200 rounded-lg overflow-hidden flex items-center justify-center">
            <Preview cameraKey={cameraKey} capturePreview={unifiedCameraUtils.capturePreview} />
          </div>
        )}

        {inputMode === 'qr' && (
          <div className="flex flex-col items-center gap-2">
            {isScanning && (
              <div className="flex items-center gap-2 text-lg text-white">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-uriYellow"></div>
                {t('checkout.scanningQR', 'Scanning for QR code...')}
              </div>
            )}
            {scanError && <div className="text-red-500 text-sm text-center">{scanError}</div>}
            <div className="text-sm text-white text-center max-w-md">
              {t(
                'checkout.qrInstructions',
                'Hold a QR code in front of your camera. The promo code will be automatically detected and applied.'
              )}
            </div>
          </div>
        )}

        {inputMode === 'keyboard' && (
          <Button
            textColor="methadone"
            onClick={() => handleSubmitPromo(input)}
            className="text-[50px]"
          >
            {t('bahela.promoModal.submit')}
          </Button>
        )}
      </div>

      {/* Input area - keyboard or QR scanner */}
      {inputMode === 'keyboard' && (
        <div className="w-full fixed bottom-0 left-0 h-2/5 bg-plaster p-4 md:p-8">
          <div id="keyboard" className="simple-keyboard" />
        </div>
      )}

      {inputMode === 'qr' && (
        <div className="w-full fixed bottom-0 left-0 bg-plaster p-4 md:p-8 flex items-center justify-center">
          <div className="text-center">
            <div id="qr-scanner-element" ref={qrElementRef} className="hidden" />
            <div className="text-lg text-carbon mb-2">
              {t('checkout.qrScannerActive', 'QR Scanner Active')}
            </div>
            <div className="text-sm text-gray-600">
              {t(
                'checkout.pointCamera',
                'Point your camera at a QR code containing the promo code'
              )}
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          .simple-keyboard {
            background: transparent;
          }
          .simple-keyboard.myTheme {
            background: transparent;
            border-radius: 0;
          }
          .simple-keyboard.myTheme .hg-button {
            height: 50px;
            display: flex;
            justify-content: center;
            align-items: center;
            background: rgba(255, 255, 255, 0.9);
            color: #333333;
            border-radius: 8px;
            font-size: 20px;
            font-family: inherit;
          }
          @media (min-width: 768px) {
            .simple-keyboard.myTheme .hg-button {
              height: 70px;
              font-size: 28px;
            }
          }
          .simple-keyboard.myTheme .hg-button:active {
            background: #1c84ff;
            color: white;
          }
          .simple-keyboard.myTheme .hg-button.hg-standardBtn {
            background: rgba(255, 255, 255, 0.9);
          }
          .simple-keyboard.myTheme .hg-button.hg-functionBtn {
            background: rgba(255, 255, 255, 0.7);
          }
        `}
      </style>
    </div>
  )
}

export default PromoModal
